import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { View, Text, TouchableOpacity, ScrollView, Platform } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { LegendList } from "@legendapp/list";
import { ActivityIndicator } from "react-native-paper";
import { Query } from "@/utils/react-query/Queries";
import Avatar from "@/components/People/Avatar";
import Unicon from "@/components/General/Unicon";
import { MENTION_ENTITIES, MENTION_SEARCH_TYPES, getMentionEntity } from "@/utils/constants/mentionEntities";

const MIN_CHARS = 2;

/**
 * MentionPicker — tabbed entity picker for @-mentions.
 *
 * Props:
 *   term        — current search term (text typed after '@')
 *   members     — array of People (conversation mode). When provided, the People
 *                 tab filters from these members client-side; all other entity tabs
 *                 still use the universal_search RPC.
 *   onConfirm   — (entity: {type, id, label}) => void
 *   onDismiss   — () => void
 */
const MentionPicker = ({ term = "", members = null, onConfirm, onDismiss, anchorRef }) => {
    const hasMembers = Array.isArray(members) && members.length > 0;
    const rpcEnabled = term.trim().length >= MIN_CHARS;

    const { data, isFetching } = Query.global.all({
        search_term: term,
        partial: true,
        match_all: false,
        types: MENTION_SEARCH_TYPES,
        options: { enabled: rpcEnabled },
    });

    //People tab: filter provided members client-side by name (when in conversation mode),
    //or use RPC results (when not restricted to members).
    const peopleResults = useMemo(() => {
        if (hasMembers) {
            const t = term.trim().toLowerCase();
            const list = members || [];
            if (!t) return list;
            return list.filter(m => `${m?.firstName || ""} ${m?.lastName || ""}`.toLowerCase().includes(t));
        }
        if (!data) return [];
        return data["people"] || [];
    }, [hasMembers, members, term, data]);

    //Build the list of visible tabs in priority order.
    const tabs = useMemo(() => {
        const result = [];
        for (const entity of MENTION_ENTITIES) {
            if (entity.type === "person") {
                if (hasMembers ? peopleResults.length > 0 : (data && Array.isArray(data["people"]) && data["people"].length > 0)) {
                    result.push(entity);
                }
            } else {
                if (data && Array.isArray(data[entity.searchKey]) && data[entity.searchKey].length > 0) {
                    result.push(entity);
                }
            }
        }
        return result;
    }, [hasMembers, peopleResults, data]);

    const [activeType, setActiveType] = useState(null);

    //Auto-select the first tab with results when results change
    useEffect(() => {
        if (tabs.length === 0) { setActiveType(null); return; }
        if (!tabs.some(t => t.type === activeType)) setActiveType(tabs[0].type);
    }, [tabs]);

    const activeEntity = getMentionEntity(activeType) || tabs[0] || MENTION_ENTITIES[0];

    const items = useMemo(() => {
        if (!activeEntity) return [];

        if (activeEntity.type === "person") {
            const source = hasMembers ? peopleResults : (data?.["people"] || []);
            return source.map(m => ({
                id: m.id,
                label: hasMembers
                    ? `${m?.firstName || ""} ${m?.lastName || ""}`.trim()
                    : m.name,
                profile: m,
            }));
        }

        if (!data) return [];
        const arr = data[activeEntity.searchKey] || [];
        return arr.map(it => ({ id: it.id, label: it.name, profile: null }));
    }, [hasMembers, peopleResults, data, activeEntity]);

    const loading = rpcEnabled && isFetching;
    const showHint = !rpcEnabled;

    const PICKER_HEIGHT = 220;
    const [pos, setPos] = useState(null);
    const useFixed = Platform.OS === 'web' && !!anchorRef;

    useLayoutEffect(() => {
        if (!useFixed) return;
        const node = anchorRef?.current;
        if (!node) return;
        const measure = () => {
            if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
                const rect = node.getBoundingClientRect();
                setPos({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
                return;
            }
            node.measureInWindow?.((x, y, w, h) => {
                if (typeof x === 'number' && typeof y === 'number') {
                    setPos({ x, y, w, h });
                }
            });
        };
        measure();
    }, [anchorRef, useFixed]);

    if (useFixed && !pos) return null;

    const fixedStyle = useMemo(() => {
        if (!useFixed || !pos) return null;
        let top = pos.y - PICKER_HEIGHT - 10;
        if (top < 10) top = pos.y + pos.h + 10;
        return { position: 'fixed', top, left: pos.x, width: pos.w, zIndex: 100, marginBottom: 0 };
    }, [useFixed, pos, PICKER_HEIGHT]);

    const renderItem = ({ item }) => (
        <TouchableOpacity
            style={styles.item}
            onPress={() => {
                onConfirm?.({ type: activeEntity.type, id: item.id, label: item.label });
                onDismiss?.();
            }}
        >
            {activeEntity.type === "person"
                ? <View style={styles.iconSpace}><Avatar profile={item.profile} size={24} disableBorder /></View>
                : <View style={styles.iconSpace}><Unicon name={activeEntity.icon} size={20} colorVariant={activeEntity.color} /></View>}
            <Text style={styles.itemText} numberOfLines={1}>{item.label}</Text>
        </TouchableOpacity>
    );

    return (
        <View style={[
            styles.picker,
            !useFixed && styles.absolute,
            fixedStyle
        ]}>
            {tabs.length > 0 && (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled" style={styles.tabBar} contentContainerStyle={styles.tabBarContent}>
                    {tabs.map(tab => (
                        <TouchableOpacity key={tab.type} style={styles.tab(tab.type === activeType)} onPress={() => setActiveType(tab.type)}>
                            <Unicon name={tab.icon} size={13} colorVariant={tab.type === activeType ? tab.color : "textSecondary"} />
                            <Text style={styles.tabText(tab.type === activeType)}>{tab.label}</Text>
                        </TouchableOpacity>
                    ))}
                </ScrollView>
            )}

            <View style={styles.listContainer}>
                <LegendList
                    keyExtractor={(item) => item.id}
                    estimatedItemSize={44}
                    data={loading ? [] : items}
                    renderItem={renderItem}
                    keyboardShouldPersistTaps="handled"
                    ListEmptyComponent={
                        loading
                            ? <View style={styles.loader}><ActivityIndicator /></View>
                            : <Text style={styles.emptyText}>{showHint ? "Type 2+ characters to search" : "No results found"}</Text>
                    }
                />
            </View>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    picker: {
        marginBottom: 10,
        zIndex: 10,
        borderRadius: 10,
        backgroundColor: colors.surface,
        boxShadow: shadows?.elevated,
        overflow: "hidden",
    },
    absolute: {
        position: "absolute",
        bottom: "100%",
        left: 0,
        right: 0,
    },
    tabBar: {
        maxHeight: 40,
        borderBottomWidth: 1,
        borderColor: colors.border,
    },
    tabBarContent: {
        alignItems: "center",
        paddingHorizontal: 4,
    },
    tab: (active) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderBottomWidth: 2,
        borderColor: active ? colors.primary : "transparent",
    }),
    tabText: (active) => ({
        fontSize: 12,
        fontFamily: active ? fonts.bold : fonts.body,
        color: active ? colors.text : colors.textSecondary,
    }),
    listContainer: {
        height: 180,
        width: "100%",
    },
    loader: {
        width: "100%",
        height: 120,
        alignItems: "center",
        justifyContent: "center",
    },
    emptyText: {
        color: colors.textSecondary,
        textAlign: "center",
        marginVertical: 16,
        fontSize: 13,
        fontFamily: fonts.body,
    },
    item: {
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderColor: colors.border,
        flexDirection: "row",
        alignItems: "center",
    },
    iconSpace: {
        marginRight: 10,
        width: 24,
        alignItems: "center",
    },
    itemText: {
        fontSize: 14,
        color: colors.text,
        fontFamily: fonts.body,
        flexShrink: 1,
    },
}));

export default MentionPicker;
